import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { showToast } from "@/components/ui/Toast";
import { playLeadChime } from "./chime";
import { leadHeadline } from "./leadLabel";
import type { NewLead } from "@/api/types/newLead";

/**
 * Fires the "a lead just arrived" burst (toast + chime + browser
 * Notification) the moment `count` increases while the app is open — never
 * on first load, and never on a same-count refetch. All side channels are
 * best-effort: browsers routinely block autoplay/notifications, and none of
 * that should ever throw or break the UI.
 *
 * Requests Notification permission once, on mount, only if it's still in
 * the browser's default (unasked) state — never re-prompts after a denial.
 */
export function useLeadArrivalAlerts(count: number, latest: NewLead | undefined) {
  const navigate = useNavigate();
  const previousCountRef = useRef<number | undefined>(undefined);
  const permissionRequestedRef = useRef(false);

  useEffect(() => {
    if (permissionRequestedRef.current) return;
    permissionRequestedRef.current = true;
    try {
      if (typeof Notification !== "undefined" && Notification.permission === "default") {
        void Notification.requestPermission().catch(() => {});
      }
    } catch {
      // Notification API unsupported — nothing to do.
    }
  }, []);

  useEffect(() => {
    const previousCount = previousCountRef.current;
    previousCountRef.current = count;

    // First load (no prior value yet) or no real increase — stay quiet.
    if (previousCount === undefined || count <= previousCount) return;

    const headline = leadHeadline(latest);
    const toastTitle = `🔔 New lead: ${headline}`;

    showToast({
      title: toastTitle,
      description: "A new website submission just landed in the pipeline.",
      variant: "info",
      duration: 10_000,
      action: { label: "View leads", onClick: () => navigate("/pipeline") },
    });

    playLeadChime();

    try {
      if (typeof Notification !== "undefined" && Notification.permission === "granted") {
        new Notification("🔔 New lead", {
          body: headline,
          tag: "foxcrm-new-lead",
        });
      }
    } catch {
      // Notifications unsupported/blocked in this environment — the toast
      // and chime already carried the alert.
    }
  }, [count, latest, navigate]);
}
